import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import type { Vertex, Preview } from '../../types';
import * as api from '../../services/api';

export interface VertexAutocompleteProps {
  onSelect: (url: string, info: string | null) => void;
  onCancel: () => void;
  excludeUrl?: string;
  excludeUrls?: string[];
  skipInfoStep?: boolean;
}

interface VertexWithPreview {
  url: string;
  preview?: Preview;
}

export function VertexAutocomplete({ onSelect, onCancel, excludeUrl, excludeUrls, skipInfoStep }: VertexAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VertexWithPreview[]>([]);
  const [allVertices, setAllVertices] = useState<VertexWithPreview[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [selectedVertex, setSelectedVertex] = useState<VertexWithPreview | null>(null);
  const [info, setInfo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const infoRef = useRef<HTMLTextAreaElement>(null);

  // Load all vertices and previews on mount
  useEffect(() => {
    loadVertices();
  }, []);

  // Focus input after loading completes
  useEffect(() => {
    if (!loading && !selectedVertex) {
      inputRef.current?.focus();
    }
  }, [loading]);

  // Focus info textarea when vertex is selected
  useEffect(() => {
    if (selectedVertex) {
      infoRef.current?.focus();
    }
  }, [selectedVertex]);

  // Filter results when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(-1);
      return;
    }

    const queryLower = query.toLowerCase();
    const excludeSet = new Set(excludeUrls || []);
    if (excludeUrl) excludeSet.add(excludeUrl);

    const filtered = allVertices.filter(v => {
      if (excludeSet.has(v.url)) return false;

      const urlMatch = v.url.toLowerCase().includes(queryLower);
      const titleMatch = v.preview?.title?.toLowerCase().includes(queryLower);
      const descMatch = v.preview?.description?.toLowerCase().includes(queryLower);

      return urlMatch || titleMatch || descMatch;
    }).slice(0, 8);

    setResults(filtered);
    setSelectedIndex(filtered.length > 0 ? 0 : -1);
  }, [query, allVertices, excludeUrl, excludeUrls]);

  async function loadVertices() {
    try {
      const vertices = await api.fetchAllVertices();
      const urls = vertices.map(v => v.vertex.rendered);
      const previews = await api.fetchPreviewsBatch(urls);

      const withPreviews: VertexWithPreview[] = vertices.map(v => ({
        url: v.vertex.rendered,
        preview: previews[v.vertex.rendered]
      }));

      setAllVertices(withPreviews);
    } catch (err) {
      console.error('[VertexAutocomplete] Failed to load vertices:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelectVertex(results[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      onCancel();
    }
  }

  function handleSelectVertex(vertex: VertexWithPreview) {
    if (skipInfoStep) {
      onSelect(vertex.url, null);
    } else {
      setSelectedVertex(vertex);
    }
  }

  function handleConfirm() {
    if (selectedVertex) {
      onSelect(selectedVertex.url, info.trim() || null);
    }
  }

  function handleBack() {
    setSelectedVertex(null);
    setInfo('');
  }

  // Confirmation step: show selected vertex with info input
  if (selectedVertex) {
    return (
      <div class="vertex-autocomplete">
        <div class="vertex-autocomplete-confirm">
          <div class="vertex-autocomplete-selected">
            <span class="vertex-autocomplete-selected-label">Adding:</span>
            <span class="vertex-autocomplete-selected-title">
              {selectedVertex.preview?.title || selectedVertex.url}
            </span>
          </div>
          <textarea
            ref={infoRef}
            class="vertex-autocomplete-info-input"
            placeholder="Add a note (optional)..."
            value={info}
            onInput={(e) => setInfo((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleBack();
              } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                handleConfirm();
              }
            }}
          />
          <div class="vertex-autocomplete-actions">
            <button class="btn-cancel" onClick={handleBack}>Back</button>
            <button class="btn-confirm" onClick={handleConfirm}>Add</button>
          </div>
        </div>
      </div>
    );
  }

  // Search step
  return (
    <div class="vertex-autocomplete">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          // Delay to allow click on results
          setTimeout(() => {
            if (!document.activeElement?.closest('.vertex-autocomplete')) {
              onCancel();
            }
          }, 150);
        }}
        placeholder={loading ? 'Loading...' : 'Search by title...'}
        disabled={loading}
        class="vertex-autocomplete-input"
      />

      {results.length > 0 && (
        <div class="vertex-autocomplete-dropdown">
          {results.map((item, index) => (
            <div
              key={item.url}
              class={`vertex-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSelectVertex(item)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {item.preview?.image && (
                <img
                  src={item.preview.image}
                  alt=""
                  class="vertex-autocomplete-image"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <div class="vertex-autocomplete-text">
                {item.preview?.title ? (
                  <>
                    <div class="vertex-autocomplete-title">{item.preview.title}</div>
                    <div class="vertex-autocomplete-url">{item.url}</div>
                  </>
                ) : (
                  <div class="vertex-autocomplete-url-only">{item.url}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
